import { usePaywallContext } from '@frontend/react-domain';
import { LinkButton, Text, TextInput, XStack, YStack } from '@frontend/web-react';
import { safeCss } from '@frontend/web-utils';
import { useRef, useState } from 'react';
import { PromotionSummaryCard } from '../../PromotionSummaryCard';
import { SubscriptionPlanSelector } from './SubscriptionPlanSelector';

export const SelectSubscriptionPlanStep = () => {
  const { paywallActions, paywallState, track } = usePaywallContext();
  const [promoCode, setPromoCode] = useState(paywallState.promotionTranslation.promoCode ?? '');
  const promoCodeInputRef = useRef<HTMLInputElement>(null);

  const handleApply = async (passedInPromoCode: string) => {
    const trimmedPromoCode = passedInPromoCode.trim();
    if (!trimmedPromoCode) {
      return;
    }

    setPromoCode(passedInPromoCode);
    const promotionDetails = await paywallActions.promotionTranslation.handleApplyPromoCode(trimmedPromoCode);
    if (promotionDetails) {
      track({
        data: {
          paywallSessionId: paywallState.navigation.paywallSessionId,
          planCode: paywallState.purchase.selectedPlanData?.planCode,
          platform: 'web',
          purchaseType: paywallState.purchase.selectedPurchaseType,
          step: paywallState.navigation.currentStep,
        },
        event: 'promoCodeApplied',
        topic: 'paywall',
      });
    } else {
      track({
        data: {
          errorCategory: 'validation',
          errorCode: 'PAYWALL_PROMO_APPLY_FAILED',
          errorSource: 'promoApply',
          paywallSessionId: paywallState.navigation.paywallSessionId,
          platform: 'web',
          purchaseType: paywallState.purchase.selectedPurchaseType,
          retriable: true,
          step: paywallState.navigation.currentStep,
        },
        event: 'promoCodeSubmitFailed',
        topic: 'paywall',
      });
    }
    promoCodeInputRef.current?.blur();
  };

  const isLoading = paywallState.promotionTranslation.isApplyPromoCodeLoading;
  const errorMessage = paywallState.promotionTranslation.promoCodeErrorMessage;

  return (
    <YStack gapY="md">
      {paywallState.promotionTranslation.hasSuccessfullyAppliedPromoCode ? (
        <YStack gapY="sm">
          <Text type="h3">Promotion applied</Text>
          <PromotionSummaryCard
            onCancel={() => {
              track({
                data: {
                  paywallSessionId: paywallState.navigation.paywallSessionId,
                  platform: 'web',
                  purchaseType: paywallState.purchase.selectedPurchaseType,
                  step: paywallState.navigation.currentStep,
                },
                event: 'promoCodeRemoved',
                topic: 'paywall',
              });
              setPromoCode('');
              paywallActions.promotionTranslation.resetPromoCode();
              promoCodeInputRef.current?.blur();
            }}
          />
        </YStack>
      ) : (
        <YStack gapY="xs" py="md">
          <Text type="h3">Promo code</Text>
          <XStack>
            <YStack flex={1}>
              <TextInput
                classNames={{ input: safeCss('rounded-md') }}
                disabled={isLoading}
                error={errorMessage}
                onChange={(value) => {
                  if (value === '') {
                    paywallActions.promotionTranslation.setPromoCodeErrorMessage('');
                  }

                  setPromoCode(value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && promoCode.trim() && !isLoading) {
                    handleApply(promoCode);
                  }
                }}
                placeholder="Enter promo code here"
                ref={promoCodeInputRef}
                rightSection={
                  <LinkButton
                    // Super hacky
                    className="mr-xxl"
                    disabled={!promoCode.trim() || isLoading}
                    isLoading={isLoading}
                    label="Apply"
                    onClick={() => handleApply(promoCode)}
                    withUnderline={false}
                  />
                }
                value={promoCode}
              />
            </YStack>
          </XStack>
        </YStack>
      )}
      <SubscriptionPlanSelector />
    </YStack>
  );
};
